import { MapPin, Home, ArrowUpFromLine, Landmark, Users, Car, TreePine, Info } from 'lucide-react';
import { INVENTORY_LABEL } from '@/lib/projectConstants';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface SnapshotTooltipContent {
  title: string;
  subtitle: string;
  paragraphs: string[];
  facts?: { label: string }[];
  internalLink?: { href: string; label: string };
}

interface SnapshotItem {
  icon: React.ElementType;
  label: string;
  value: string;
  subtext: string;
  color: string;
  tooltipContent: SnapshotTooltipContent;
}

const snapshots: SnapshotItem[] = [
  {
    icon: MapPin,
    label: 'Location',
    value: 'Chandivali–Powai',
    subtext: 'Mumbai',
    color: 'from-amber-500 to-orange-500',
    tooltipContent: {
      title: 'An Established Residential Micro-Market in Mumbai',
      subtitle: 'Chandivali–Powai Location Advantage',
      paragraphs: [
        'Chandivali–Powai represents one of Mumbai\'s most mature residential micro-markets, offering a rare balance between central connectivity and long-term livability. Strategically positioned between Andheri, Powai, and the eastern business corridors, the area benefits from seamless access to JVLR, Saki Naka, and upcoming metro infrastructure.',
        'Nahar Amrit Shakti township played a pivotal role in shaping Chandivali\'s evolution from an industrial belt into a planned residential ecosystem. With wide internal roads, integrated green spaces, and a strong social infrastructure, the area has consistently attracted end-users seeking stability over speculative growth.',
        'Unlike hyper-dense pockets, Chandivali–Powai offers measured development, making it a preferred choice for families, professionals, and long-term homeowners prioritising quality of life.',
      ],
      facts: [
        { label: 'JVLR connectivity' },
        { label: 'Upcoming Sakinaka Metro' },
        { label: 'Established township ecosystem' },
        { label: 'Wide internal roads' },
      ],
      internalLink: { href: '/location', label: 'Explore Full Location Details' },
    },
  },
  {
    icon: Home,
    label: 'Residences',
    value: INVENTORY_LABEL,
    subtext: 'From 1,069 sq.ft.',
    color: 'from-emerald-500 to-teal-500',
    tooltipContent: {
      title: 'Thoughtfully Planned Large-Format Homes',
      subtitle: '3 & 4 BHK Residences',
      paragraphs: [
        'Large-format 3 and 4 BHK residences are increasingly rare in central Mumbai due to land fragmentation and density pressures. At Nahar developments, home configurations are designed to prioritise usable carpet area, efficient layouts, and long-term adaptability.',
        'The planning philosophy draws from decades of experience in township-scale development at Nahar Amrit Shakti, where homes were designed not just for immediate occupation, but for generational use—balancing private spaces, common areas, and natural light.',
        'Such configurations appeal to families upgrading within the micro-market, as well as buyers seeking permanence rather than transitional housing.',
      ],
      facts: [
        { label: '3 & 4 BHK options' },
        { label: 'From 1,069 sq.ft. carpet' },
        { label: 'Generational design philosophy' },
        { label: 'Efficient layouts' },
      ],
      internalLink: { href: '/floor-plans', label: 'View All Floor Plans' },
    },
  },
  {
    icon: ArrowUpFromLine,
    label: 'Ceiling',
    value: '10+ Ft Clear',
    subtext: 'Rare height',
    color: 'from-blue-500 to-indigo-500',
    tooltipContent: {
      title: 'Vertical Space as a Livability Advantage',
      subtitle: '10+ Ft Clear Ceiling Height',
      paragraphs: [
        'Clear ceiling height is a critical yet often overlooked factor in residential comfort. A 10+ ft clear height significantly improves natural ventilation, daylight penetration, and spatial perception within the home.',
        'This design approach has been consistently adopted across premium Nahar developments, including within the Nahar Amrit Shakti ecosystem, where architectural decisions prioritised long-term comfort over short-term cost optimisation.',
        'Higher clear heights also enhance interior flexibility, allowing for better air circulation, future design adaptations, and improved thermal performance—particularly relevant in Mumbai\'s coastal climate.',
      ],
      facts: [
        { label: '10+ ft clear height' },
        { label: 'Improved ventilation' },
        { label: 'Better daylight penetration' },
        { label: 'Enhanced spatial perception' },
      ],
      internalLink: { href: '/clear-height', label: 'Read More: 10+ Ft Clear Height' },
    },
  },
  {
    icon: Landmark,
    label: 'Land',
    value: 'Freehold',
    subtext: 'Permanent',
    color: 'from-purple-500 to-violet-500',
    tooltipContent: {
      title: 'Permanent Ownership Without Lease Dependency',
      subtitle: 'Freehold Land Ownership',
      paragraphs: [
        'Freehold land ownership ensures that homeowners retain perpetual rights over their property without lease renewals or authority dependencies. In Mumbai, where many developments sit on leasehold parcels, freehold status offers a clear legal and financial advantage.',
        'Nahar Group has historically prioritised freehold land parcels for its large-scale developments, including Nahar Amrit Shakti, enabling long-term certainty for homeowners and intergenerational asset security.',
        'For end-users and investors alike, freehold ownership simplifies succession, resale, and financing—making it a foundational advantage rather than a lifestyle amenity.',
      ],
      facts: [
        { label: 'Perpetual ownership rights' },
        { label: 'No lease renewals' },
        { label: 'Simplified succession' },
        { label: 'Better financing options' },
      ],
      internalLink: { href: '/freehold-land', label: 'Learn More About Freehold Ownership' },
    },
  },
  {
    icon: Users,
    label: 'Density',
    value: '4/Floor',
    subtext: 'Low-density',
    color: 'from-rose-500 to-pink-500',
    tooltipContent: {
      title: 'Low-Density Living in a High-Density City',
      subtitle: '4 Residences per Floor',
      paragraphs: [
        'Low-density residential planning directly impacts privacy, noise levels, and community experience. Limiting the number of residences per floor reduces congestion in common areas and enhances exclusivity.',
        'This principle has been a defining feature of Nahar\'s township planning philosophy, most notably within Nahar Amrit Shakti, where human-scale living was prioritised despite large land parcels.',
        'In a city increasingly characterised by vertical crowding, low-density design remains one of the strongest indicators of long-term residential quality.',
      ],
      facts: [
        { label: 'Only 4 units per floor' },
        { label: 'Enhanced privacy' },
        { label: 'Reduced common area congestion' },
        { label: 'Human-scale community' },
      ],
      internalLink: { href: '/low-density-design', label: 'Learn More About Low-Density Design' },
    },
  },
  {
    icon: Car,
    label: 'Parking',
    value: 'Covered',
    subtext: 'Direct access',
    color: 'from-cyan-500 to-sky-500',
    tooltipContent: {
      title: 'Ease of Access Without Compromising Ventilation',
      subtitle: 'Covered Surface Parking',
      paragraphs: [
        'Covered surface parking offers practical advantages over deep-basement parking, including better ventilation, easier access, and improved safety.',
        'Within Nahar Amrit Shakti township, surface and podium-level parking was integrated as part of the overall planning strategy, reducing dependency on mechanical ventilation while enhancing everyday usability.',
        'Such parking solutions are particularly valuable in residential developments focused on family living and long-term occupation, where convenience and safety outweigh maximum parking stack optimisation.',
      ],
      facts: [
        { label: 'Natural ventilation' },
        { label: 'Easier vehicle access' },
        { label: 'Enhanced safety' },
        { label: 'Family-friendly design' },
      ],
      internalLink: { href: '/covered-parking', label: 'Read More: Covered Surface Parking' },
    },
  },
];

// Miyawaki content - kept separate for special styling
const miyawakiContent: SnapshotTooltipContent = {
  title: 'A Commitment to Reforestation in Urban India',
  subtitle: 'Miyawaki Urban Forest',
  paragraphs: [
    'At Nahar Group, sustainability is not an add-on—it is a core philosophy. We believe in maintaining a long-term balance between human development and nature, especially in dense urban environments like Mumbai.',
    'A living embodiment of this commitment is the Nahar Amrit Shakti Udyan, located within Nahar\'s Amrit Shakti township at Chandivali. Spanning approximately 34,000 square metres, this urban forest has been developed using the Miyawaki reforestation methodology, pioneered by renowned Japanese botanist Akira Miyawaki.',
    'The forest comprises over 41,000 trees, representing 79 carefully selected native and exotic species, supported by layered shrubs and ground flora planted in enriched soil systems. The Miyawaki technique enables rapid, dense, and self-sustaining growth, resulting in significantly higher biodiversity, oxygen output, and micro-climate cooling compared to conventional landscaping.',
    'The scale and impact of this initiative have received national recognition, including a mention by the Hon\'ble Prime Minister of India, Shri Narendra Modi, during Mann Ki Baat, where the Nahar Amrit Shakti Udyan was acknowledged as one of the largest Miyawaki forests in Mumbai.',
    'This philosophy of ecological responsibility directly informs the planning ethos of Nahar developments—where green infrastructure is treated as permanent urban capital, not cosmetic landscaping.',
  ],
  facts: [
    { label: '34,000+ sq. m. urban forest' },
    { label: '41,000+ trees' },
    { label: '79 plant species' },
    { label: 'Miyawaki methodology' },
  ],
  internalLink: { href: '/sustainability', label: 'Explore Sustainability Initiatives' },
};

const ProjectSnapshot = () => {
  const [activeDialog, setActiveDialog] = useState<string | null>(null);

  const openDialog = (label: string) => setActiveDialog(label);
  const closeDialog = () => setActiveDialog(null);

  // Generate ItemList schema for SEO
  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Nahar Chandi Valley Key Features',
    description: 'Foundational advantages that define Nahar Chandi Valley residential project in Chandivali, Mumbai',
    numberOfItems: snapshots.length + 1,
    itemListElement: [
      ...snapshots.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.value,
        description: item.tooltipContent.title,
      })),
      {
        '@type': 'ListItem',
        position: snapshots.length + 1,
        name: 'Miyawaki Urban Forest',
        description: miyawakiContent.title,
      },
    ],
  };

  return (
    <section className="relative z-10 py-8" aria-label="Project key features">
      {/* Schema.org ItemList for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />
      
      <div className="container mx-auto px-4">
        <div className="flex flex-wrap justify-center gap-3 md:gap-4">
          {snapshots.map((item, index) => (
            <button
              key={item.label}
              onClick={() => openDialog(item.label)}
              className="flex items-center gap-3 px-4 py-3 bg-card/95 backdrop-blur-sm rounded-full shadow-md border border-border/50 hover:shadow-lg hover:scale-105 transition-all duration-300 animate-fade-in-up cursor-pointer group"
              style={{ animationDelay: `${index * 80}ms` }}
              aria-label={`Learn more about ${item.value}`}
            >
              <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                <item.icon className="w-4 h-4 text-white" />
              </div>
              <div className="text-left">
                <p className="font-display text-sm font-bold text-foreground leading-tight">{item.value}</p>
                <p className="font-sans text-xs text-muted-foreground leading-tight">{item.subtext}</p>
              </div>
              <Info className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
            </button>
          ))}
          
          {/* Miyawaki Urban Forest - Special Interactive Pill */}
          <button
            onClick={() => openDialog('Miyawaki')}
            className="flex items-center gap-3 px-4 py-3 bg-card/95 backdrop-blur-sm rounded-full shadow-md border border-border/50 hover:shadow-lg hover:scale-105 transition-all duration-300 animate-fade-in-up cursor-pointer group"
            style={{ animationDelay: `${snapshots.length * 80}ms` }}
            aria-label="Learn more about Miyawaki Urban Forest"
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <TreePine className="w-4 h-4 text-white" />
            </div>
            <div className="text-left">
              <p className="font-display text-sm font-bold text-foreground leading-tight">Miyawaki Forest</p>
              <p className="font-sans text-xs text-muted-foreground leading-tight">Dense native reforestation</p>
            </div>
            <Info className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
          </button>
        </div>
      </div>

      {/* Feature Dialogs - All pills */}
      {snapshots.map((item) => (
        <Dialog key={item.label} open={activeDialog === item.label} onOpenChange={closeDialog}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center shadow-sm`}>
                  <item.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <DialogTitle className="font-display text-xl">{item.tooltipContent.title}</DialogTitle>
                  <p className="text-xs text-muted-foreground font-sans">{item.tooltipContent.subtitle}</p>
                </div>
              </div>
              <DialogDescription className="sr-only">
                Learn more about {item.value}
              </DialogDescription>
            </DialogHeader>
            
            <article className="space-y-4 text-sm text-muted-foreground leading-relaxed">
              {item.tooltipContent.paragraphs.map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
              
              {/* Key Facts */}
              {item.tooltipContent.facts && (
                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-border">
                  {item.tooltipContent.facts.map((fact, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full bg-gradient-to-br ${item.color}`}></span>
                      <span className="text-xs">{fact.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Internal Link */}
              {item.tooltipContent.internalLink && (
                <div className="pt-4">
                  <Link to={item.tooltipContent.internalLink.href}>
                    <Button variant="outline" size="sm" className="w-full text-accent border-accent hover:bg-accent hover:text-accent-foreground">
                      {item.tooltipContent.internalLink.label} →
                    </Button>
                  </Link>
                </div>
              )}
            </article>
          </DialogContent>
        </Dialog>
      ))}

      {/* Miyawaki Forest Dialog - Special */}
      <Dialog open={activeDialog === 'Miyawaki'} onOpenChange={closeDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-sm">
                <TreePine className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="font-display text-xl">{miyawakiContent.title}</DialogTitle>
                <p className="text-xs text-muted-foreground font-sans">{miyawakiContent.subtitle}</p>
              </div>
            </div>
            <DialogDescription className="sr-only">
              Learn about Nahar Group's Miyawaki Urban Forest initiative
            </DialogDescription>
          </DialogHeader>
          
          <article className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            {miyawakiContent.paragraphs.map((paragraph, idx) => (
              <p key={idx}>{paragraph}</p>
            ))}
            
            {/* Micro-Facts */}
            {miyawakiContent.facts && (
              <div className="grid grid-cols-2 gap-3 pt-4 border-t border-border">
                {miyawakiContent.facts.map((fact, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    <span className="text-xs">{fact.label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Internal Link */}
            {miyawakiContent.internalLink && (
              <div className="pt-4">
                <Link to={miyawakiContent.internalLink.href}>
                  <Button variant="outline" size="sm" className="w-full text-accent border-accent hover:bg-accent hover:text-accent-foreground">
                    {miyawakiContent.internalLink.label} →
                  </Button>
                </Link>
              </div>
            )}
          </article>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ProjectSnapshot;
